import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MODULES, ROLE_LABELS } from "@/lib/constants";
import {
  Sparkles, Users, ShieldAlert, ArrowUpRight, Loader2,
  Instagram, Facebook, Music2, MessageCircle, Mail, Calendar as CalIcon,
  Send, Eye, Wand2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ICON_MAP = { Instagram, Facebook, Music2, MessageCircle, Mail, Sparkles };

const PLATFORM_META = {
  instagram: { color: "#E1306C", label: "Instagram" },
  facebook: { color: "#1877F2", label: "Facebook" },
  tiktok: { color: "#1A1D1B", label: "TikTok" },
  whatsapp: { color: "#25D366", label: "WhatsApp" },
  email: { color: "#00A36C", label: "Email" },
};

function StatCard({ label, value, hint, testid, accent }) {
  return (
    <div className="card-elevated p-6" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-stone-400 font-semibold">
        {label}
      </div>
      <div className={`mt-3 font-serif-display text-4xl font-semibold ${accent || "text-brand-ink"}`}>
        {value}
      </div>
      {hint && <div className="mt-2 text-xs text-stone-500">{hint}</div>}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function Dashboard() {
  const { user, isMaster, hasModule } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar estadísticas");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const enabledModules = MODULES.filter((m) => hasModule(m.key));

  return (
    <div className="space-y-10 animate-fade-in-up" data-testid="dashboard-page">
      {/* Welcome */}
      <section className="card-elevated relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-emerald/10 blur-3xl" />
        <div className="absolute -left-24 -bottom-24 h-64 w-64 rounded-full bg-brand-gold/20 blur-3xl" />
        <div className="relative p-8 lg:p-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="divider-gold mb-4" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold">
              Panel principal
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl font-semibold text-brand-ink">
              Hola, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-3 text-stone-600 text-base leading-relaxed">
              Bienvenido a <strong>FStudy Manager</strong>. Aquí encontrará el pulso
              del estudio: publicaciones, campañas, piezas creativas y próximas
              programaciones.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-emerald-soft border border-brand-emerald/20 px-4 py-1.5 text-xs font-semibold text-brand-emerald-dark">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald" />
              Rol: {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
          <div className="shrink-0">
            <img src="/assets/logo.png" alt="" className="h-28 w-28 object-contain opacity-90" />
          </div>
        </div>
      </section>

      {/* Real Stats */}
      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : (
        <>
          <section>
            <div className="mb-5">
              <h2 className="font-serif-display text-2xl text-brand-ink">Resumen del mes</h2>
              <p className="text-sm text-stone-500 mt-0.5">Estadísticas reales de {user?.role === "master" ? "todo el equipo" : "sus módulos habilitados"}.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <StatCard
                label="Publicaciones este mes"
                value={stats?.posts_month?.total ?? 0}
                hint={`${stats?.posts_month?.by_status?.published ?? 0} publicadas · ${stats?.posts_month?.by_status?.scheduled ?? 0} programadas · ${stats?.posts_month?.by_status?.draft ?? 0} borradores`}
                testid="stat-posts"
              />
              {stats?.email_month ? (
                <>
                  <StatCard
                    label="Campañas enviadas"
                    value={stats.email_month.campaigns_sent}
                    hint={`${stats.email_month.total_recipients} destinatarios totales`}
                    testid="stat-campaigns"
                  />
                  <StatCard
                    label="Tasa de apertura"
                    value={`${stats.email_month.open_rate_avg}%`}
                    hint={`${stats.email_month.total_opens} de ${stats.email_month.total_recipients} correos`}
                    testid="stat-open-rate"
                    accent="text-brand-emerald-dark"
                  />
                </>
              ) : (
                <StatCard label="Email" value="—" hint="Módulo no habilitado" testid="stat-email-disabled" />
              )}
              {stats?.creative ? (
                <StatCard
                  label="Piezas creativas"
                  value={stats.creative.approved}
                  hint={`${stats.creative.pending} pendientes · ${stats.creative.rejected} rechazadas`}
                  testid="stat-creative"
                />
              ) : (
                <StatCard label="Motor Creativo" value="—" hint="Módulo no habilitado" testid="stat-creative-disabled" />
              )}
            </div>
          </section>

          {/* Upcoming */}
          {stats?.upcoming && stats.upcoming.length > 0 && (
            <section>
              <div className="mb-5">
                <h2 className="font-serif-display text-2xl text-brand-ink">Próximos eventos</h2>
                <p className="text-sm text-stone-500 mt-0.5">Publicaciones y campañas programadas.</p>
              </div>
              <div className="card-elevated divide-y divide-border" data-testid="upcoming-events">
                {stats.upcoming.map((e) => {
                  const meta = PLATFORM_META[e.platform] || PLATFORM_META.email;
                  return (
                    <Link key={e.id} to={e.target_url} className="flex items-center gap-4 p-4 hover:bg-stone-50 transition-colors" data-testid={`upcoming-${e.id}`}>
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: meta.color }}>
                        {e.kind === "campaign" ? <Mail className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-brand-ink line-clamp-1">{e.title || "(sin título)"}</div>
                        <div className="text-xs text-stone-500">{meta.label} · {fmt(e.at)}</div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-stone-300 shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {/* Enabled modules shortcuts */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-serif-display text-2xl text-brand-ink">Mis módulos</h2>
            <p className="text-sm text-stone-500 mt-0.5">Accesos habilitados para su rol.</p>
          </div>
        </div>
        {enabledModules.length === 0 ? (
          <div className="card-elevated p-10 text-center text-stone-500">
            No tiene módulos habilitados. Contacte al usuario Master para solicitar acceso.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {enabledModules.map((m) => {
              const Icon = ICON_MAP[m.icon] || Sparkles;
              return (
                <Link key={m.key} to={m.path} data-testid={`module-card-${m.key}`} className="card-elevated p-6 flex items-center justify-between group hover:border-brand-emerald hover:shadow-emerald transition-all">
                  <div className="flex items-center gap-4">
                    <div className="h-11 w-11 rounded-lg bg-brand-emerald-soft text-brand-emerald-dark flex items-center justify-center">
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="font-serif-display text-lg text-brand-ink font-semibold">{m.label}</div>
                      <div className="text-xs text-stone-500">Abrir módulo</div>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-stone-300 group-hover:text-brand-emerald transition-colors" />
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Master shortcuts */}
      {isMaster && (
        <section>
          <div className="mb-5">
            <h2 className="font-serif-display text-2xl text-brand-ink">Administración</h2>
            <p className="text-sm text-stone-500 mt-0.5">Herramientas exclusivas del rol Master.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/equipo" data-testid="master-shortcut-team" className="card-elevated p-6 flex items-center justify-between group hover:border-brand-emerald hover:shadow-emerald transition-all">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-brand-emerald text-white flex items-center justify-center"><Users className="h-5 w-5" /></div>
                <div>
                  <div className="font-serif-display text-xl text-brand-ink font-semibold">Gestión de Equipo</div>
                  <div className="text-xs text-stone-500 mt-0.5">Roles, módulos y estado</div>
                </div>
              </div>
              <ArrowUpRight className="h-5 w-5 text-stone-300 group-hover:text-brand-emerald" />
            </Link>
            <Link to="/auditoria" data-testid="master-shortcut-audit" className="card-elevated p-6 flex items-center justify-between group hover:border-brand-emerald hover:shadow-emerald transition-all">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-brand-gold text-brand-ink flex items-center justify-center"><ShieldAlert className="h-5 w-5" /></div>
                <div>
                  <div className="font-serif-display text-xl text-brand-ink font-semibold">Auditoría</div>
                  <div className="text-xs text-stone-500 mt-0.5">Historial completo</div>
                </div>
              </div>
              <ArrowUpRight className="h-5 w-5 text-stone-300 group-hover:text-brand-emerald" />
            </Link>
            <Link to="/calendario" data-testid="master-shortcut-calendar" className="card-elevated p-6 flex items-center justify-between group hover:border-brand-emerald hover:shadow-emerald transition-all">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-brand-emerald-dark text-white flex items-center justify-center"><CalIcon className="h-5 w-5" /></div>
                <div>
                  <div className="font-serif-display text-xl text-brand-ink font-semibold">Calendario Global</div>
                  <div className="text-xs text-stone-500 mt-0.5">Vista unificada</div>
                </div>
              </div>
              <ArrowUpRight className="h-5 w-5 text-stone-300 group-hover:text-brand-emerald" />
            </Link>
          </div>
          {/* Fase 29 · Widget: cuentas Composio detectadas sin vincular. */}
          <UnlinkedComposioAccounts />
        </section>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Fase 29 · Widget en Dashboard Master · muestra cuentas Composio detectadas
// que aún no están vinculadas a una social_connection local (para activar el
// modo REAL en un clic). CTA lleva a /conectividad.
// -----------------------------------------------------------------------------
function UnlinkedComposioAccounts() {
  const [state, setState] = React.useState({ loading: true, unlinked: 0, byToolkit: {} });
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [accResp, connResp] = await Promise.all([
          api.get("/integrations/composio/accounts").catch(() => ({ data: { accounts: [] } })),
          api.get("/social/connections").catch(() => ({ data: [] })),
        ]);
        const accounts = accResp.data?.accounts || [];
        const conns = connResp.data || [];
        const linkedIds = new Set(conns.map((c) => c.composio_account_id).filter(Boolean));
        const unlinked = accounts.filter((a) => !linkedIds.has(a.id));
        const byToolkit = unlinked.reduce((acc, a) => {
          const k = (a.toolkit || "otro").toLowerCase();
          acc[k] = (acc[k] || 0) + 1; return acc;
        }, {});
        if (alive) setState({ loading: false, unlinked: unlinked.length, byToolkit });
      } catch {
        if (alive) setState({ loading: false, unlinked: 0, byToolkit: {} });
      }
    })();
    return () => { alive = false; };
  }, []);
  if (state.loading || state.unlinked === 0) return null;
  return (
    <div className="mt-4 rounded-2xl border-2 border-dashed border-brand-gold/60 bg-brand-gold-soft/40 p-5 flex items-center justify-between flex-wrap gap-3"
         data-testid="unlinked-composio-widget">
      <div>
        <div className="text-xs uppercase tracking-widest font-semibold text-brand-gold-dark mb-1">
          Cuentas por vincular
        </div>
        <div className="font-serif-display text-2xl text-brand-ink">
          Hay {state.unlinked} cuenta{state.unlinked > 1 ? "s" : ""} Composio detectada{state.unlinked > 1 ? "s" : ""} sin vincular
        </div>
        <div className="text-sm text-stone-600 mt-1">
          Vincúlalas para activar el modo REAL: {Object.entries(state.byToolkit).map(([k, v]) => `${k} (${v})`).join(" · ")}.
        </div>
      </div>
      <Link
        to="/conectividad"
        data-testid="unlinked-composio-cta"
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-brand-gold text-brand-ink font-semibold hover:bg-brand-gold-dark hover:text-white transition"
      >
        Vincular ahora <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
